import React, { useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { Typography } from 'src/components/new/Typography';
import EditDocumentsButton from './EditDocumentsButton';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { apiInstance } from 'src/api/apiInstance';
import ServiceBadge from 'src/components/service-badge/ServiceBadge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ServiceType = 'normal' | 'rush';

export interface EditPreferencesSectionHandle {
  hasUnsavedChanges: () => boolean;
  discardChanges: () => void;
}

interface PreferenceItem {
  label: string;
  value: React.ReactNode;
}

interface EditPreferencesSectionProps {
  /** All preference items rendered in read-only mode (same array as before) */
  items: PreferenceItem[];
  /** Current service type from the job */
  currentService: ServiceType;
  /** Job ID used for the API call */
  jobId: number;
  /** Whether the current user is a CoS admin */
  isCosAdmin: boolean;
  /** Job status — edit button is only shown for on_hold_manual */
  jobStatus: string;
  /**
   * Called with the recalculated invoice payload when the save succeeds.
   * Mirrors the onInvoiceRecalculated callback pattern from JobDocuments.
   */
  onInvoiceRecalculated?: (invoice: any) => void;
  /**
   * Reports whether there are unsaved preference changes, so the parent can
   * gate the unsaved-changes dialog on panel close.
   */
  onUnsavedChangesChange?: (hasChanges: boolean) => void;
  /** Called after a successful save so the parent can refresh audit logs etc. */
  onSaveComplete?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SERVICE_OPTIONS: { label: string; value: ServiceType }[] = [
  { label: 'Normal', value: 'normal' },
  { label: 'Rush', value: 'rush' },
];

const EditPreferencesSection = forwardRef<
  EditPreferencesSectionHandle,
  EditPreferencesSectionProps
>(
  (
    {
      items,
      currentService,
      jobId,
      isCosAdmin,
      jobStatus,
      onInvoiceRecalculated,
      onUnsavedChangesChange,
      onSaveComplete,
    },
    ref,
  ) => {
    const [isEditMode, setIsEditMode] = useState(false);
    // savedService tracks the last persisted value. It starts from the prop
    // and is updated locally after a successful save so the UI immediately
    // reflects the new state without waiting for the parent to re-fetch the job.
    const [savedService, setSavedService] = useState<ServiceType>(currentService);
    const [selectedService, setSelectedService] = useState<ServiceType>(currentService);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Only sync from the prop when not in edit mode (i.e. the parent re-fetched
    // a fresh job object). While the user is actively editing, leave the local
    // state alone so in-progress changes aren't clobbered.
    useEffect(() => {
      if (!isEditMode) {
        setSavedService(currentService);
        setSelectedService(currentService);
      }
    }, [currentService]);

    const hasChanges = isEditMode && selectedService !== savedService;

    // Report unsaved-changes state upward so the parent can show the
    // UnsavedChangesDialog on panel close (same pattern as JobDocuments).
    useEffect(() => {
      onUnsavedChangesChange?.(hasChanges);
    }, [hasChanges, onUnsavedChangesChange]);

    // Reset gate on unmount
    useEffect(() => {
      return () => {
        onUnsavedChangesChange?.(false);
      };
    }, [onUnsavedChangesChange]);

    // Expose imperative handle so DetailedJobView can query / act on this
    // section when the user tries to close the panel with unsaved changes.
    useImperativeHandle(
      ref,
      () => ({
        hasUnsavedChanges: () => hasChanges,
        discardChanges: handleDiscard,
      }),
      [hasChanges, selectedService, savedService],
    );

    const handleToggleEditMode = () => {
      if (isEditMode) {
        handleDiscard();
      } else {
        setSelectedService(savedService);
        setError(null);
        setIsEditMode(true);
      }
    };

    const handleDiscard = () => {
      setSelectedService(savedService);
      setIsEditMode(false);
      setError(null);
    };

    const handleSave = async () => {
      if (!hasChanges || isSaving) return;
      setIsSaving(true);
      setError(null);
      try {
        const response = await apiInstance.put(`/job/${jobId}/service-type`, {
          service: selectedService,
        });
        // Commit the new value as the persisted baseline so read-mode shows
        // the updated service and "discard" resets to the new value correctly.
        setSavedService(selectedService);
        onInvoiceRecalculated?.(response.data.data);
        onSaveComplete?.();
        setIsEditMode(false);
        setSuccessMessage('Service type updated successfully');
      } catch (err: any) {
        const message =
          err?.response?.data?.message ||
          'Error saving service type. Check your connection and try again.';
        setError(message);
      } finally {
        setIsSaving(false);
      }
    };

    const showEditButton = isCosAdmin;

    // Build display items. In read mode the Service row shows savedService
    // (the last persisted value) so it stays correct after a save without
    // the parent needing to re-fetch. In edit mode it shows the dropdown.
    const displayItems = items.map((item) => {
      if (item.label === 'Service') {
        return {
          ...item,
          value: <ServiceBadge service={savedService} />,
        };
      }
      return item;
    });

    return (
      <div
        style={{
          backgroundColor: isEditMode ? 'rgba(67, 110, 178, 0.10)' : 'transparent',
          padding: isEditMode ? '1rem 1.25rem' : '0',
          margin: isEditMode ? '20px -1.25rem' : '0',
          marginTop: isEditMode ? '20px' : '15px',
        }}
      >
        {/* ── Section header ─────────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.9375rem',
          }}
        >
          <Typography sx={{ fontWeight: '600' }}>Preferences</Typography>
          {showEditButton && (
            <EditDocumentsButton
              onClick={handleToggleEditMode}
              label={isEditMode ? 'Close Edit Mode' : 'Edit Preferences'}
            />
          )}
        </div>

        {/* ── Content ────────────────────────────────────────────────────── */}
        <div
          style={{
            padding: '0.9375rem',
            border: '0.0625rem solid #cac4d0',
            borderRadius: '0rem',
          }}
        >
          {displayItems.map(
            (item) =>
              item.value && (
                <div key={item.label} style={{ marginBottom: '0.75rem' }}>
                  {isEditMode && item.label === 'Service' ? (
                    /* ── Editable Service row ─────────────────────────── */
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Typography sx={{ fontWeight: '500', flexShrink: 0 }}>
                        Service:{' '}
                      </Typography>
                      <Select
                        value={selectedService}
                        onChange={(e: SelectChangeEvent<ServiceType>) =>
                          setSelectedService(e.target.value as ServiceType)
                        }
                        size="small"
                        sx={{
                          minWidth: '8rem',
                          fontSize: '0.875rem',
                          fontFamily: 'Instrument Sans, Arial',
                          backgroundColor: '#fff',
                        }}
                      >
                        {SERVICE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  ) : (
                    /* ── Read-only row ────────────────────────────────── */
                    <Typography sx={{ marginBottom: 0 }}>
                      <span style={{ fontWeight: '500' }}>{item.label}: </span>
                      {item.value}
                    </Typography>
                  )}
                </div>
              ),
          )}
        </div>

        {/* ── Edit-mode action bar ────────────────────────────────────────── */}
        {isEditMode && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1.5rem',
            }}
          >
            <button
              onClick={hasChanges ? handleDiscard : undefined}
              disabled={!hasChanges}
              style={{
                background: 'none',
                border: 'none',
                color: hasChanges ? '#d32f2f' : '#ACACAC',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: hasChanges ? 'pointer' : 'not-allowed',
                textDecoration: 'underline',
                padding: 0,
              }}
            >
              Discard Changes
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              style={{
                backgroundColor: hasChanges ? '#436EB2' : '#9e9e9e',
                color: '#fff',
                border: 'none',
                padding: '0.625rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: hasChanges && !isSaving ? 'pointer' : 'not-allowed',
                opacity: isSaving ? 0.7 : 1,
              }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}

        {/* ── Snackbars ──────────────────────────────────────────────────── */}
        <BkSnackBar
          open={!!error}
          snackMessage={error || ''}
          handleCloseSnackbar={() => setError(null)}
          severity="error"
        />
        <BkSnackBar
          open={!!successMessage}
          snackMessage={successMessage || ''}
          handleCloseSnackbar={() => setSuccessMessage(null)}
          severity="success"
        />
      </div>
    );
  },
);

export default EditPreferencesSection;
